import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import {
  getBarDetail,
  postBarInformation,
  postVerifyBusinessNumber,
  putBarInformation,
} from "store/bar-list/thunk";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import Keywords from "components/Common/Keywords";
import {
  COLOR_BAR_LIST_OPEN_STATUS,
  COLOR_BAR_LIST_PARTNER_STATUS,
  COLOR_BAR_LIST_PLAN,
  LIST_OPTION_BAR_LIST_OPEN_STATUS,
  LIST_OPTION_BAR_LIST_PARTNER_STATUS,
  LIST_OPTION_BAR_LIST_PLAN,
} from "helpers/constans";
import NaverMap from "components/Common/NaverMap";
import DropdownAsyncBarType from "components/Common/DropdownAsyncBarType";
import UploadImage from "components/Common/UploadImage";
import SearchAddress from "components/Common/SearchAddress";
import userApi from "api/userApi";
import UploadImageSingle from "components/Common/UploadImageSingle";

export interface Props {
  isModal?: boolean;
  id?: string;
  triggerRefresh?: () => void;
  setLinkImage?: any;
  isUpdate?: boolean;
  setTitleBarDetail?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

const BarDetailForm = ({
  id = "",
  triggerRefresh,
  setLinkImage,
  isUpdate,
  setTitleBarDetail
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [checkedBarType, setCheckedBarType] = useState<any>(null);
  const [isOpenSearchAddress, setIsOpenSearchAddress] =
    useState<boolean>(false);

  const handleSubmit = async (values: any) => {
    try {
      if (values?.business_number) {
        const res = await handleVerifyBusinessNumber(values?.business_number);
        if (res === false) return;
      }

      setIsLoading((_prev) => true);
      const hagTag = values?.hashtag?.join(", ");

      const data = {
        title: values?.title,
        nickname: values?.title,
        description: values?.description,
        phone: values?.phone,
        address: values?.address,
        // region1: values?.region1,
        // region2: values?.region2,
        // region3: values?.region3,
        representative_name: values?.representative_name,
        business_number: values?.business_number,
        lat: values?.lat,
        long: values?.long,
        files: values?.files,
        type: values?.type,
        plan: values?.plan,
        hashtag: hagTag ?? "",
        is_active: values?.is_active === 1 ? true : false,
        background_url: values?.background_url,
        thumbnail_url:
          typeof values?.thumbnail_url == "string"
            ? values?.thumbnail_url
            : values?.thumbnail_url?.[0]?.url,
        partner_status: values?.partner_status,
      };

      const response: any = id
        ? await putBarInformation(id, data)
        : await postBarInformation(data);
      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh?.();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    title: Yup.string().required(`${t("Name is required")}`),
    address: Yup.string().required(`${t("Address is required")}`),
    thumbnail_url: Yup.string().required(`${t("Thumbnail is required")}`),
    background_url: Yup.array()
      .min(1, `${t("Background is required")}`)
      .required(`${t("Background is required")}`),
    // files: Yup.array()
    //   .min(1, `${t("This field is required")}`)
    //   .required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      nickname: "",
      description: "",
      phone: "",
      address: "",
      // region1: "",
      // region2: "",
      // region3: "",
      lat: "",
      long: "",
      representative_name: "",
      business_number: "",
      files: null,
      background_url: null,
      thumbnail_url: "",
      type: "",
      plan: "",
      hashtag: [],
      search_keyword: "",
      is_active: 1,
      partner_status: "",
      short_address: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
    validateOnChange: true,
    // validateOnBlur: submitted,
  });

  const initialValueHashTag =
    (formik?.values?.hashtag?.map((item: any) => ({
      id: item?.toLowerCase(),
      text: item,
    })) as any) || [];

  const handleSetValueForm = (valueDefault: any) => {
    if (setTitleBarDetail) {
      setTitleBarDetail(valueDefault?.title)
    }

    formik.setValues(valueDefault);
    formik.setFieldValue(
      "business_number",
      valueDefault?.user_profile?.business_number
    );
    formik.setFieldValue(
      "representative_name",
      valueDefault?.representative_name
    );

    if (valueDefault?.type && valueDefault?.type_id) {
      formik.setFieldValue("type", valueDefault?.type_id);
      setCheckedBarType({
        label:
          i18n?.language === "ko"
            ? valueDefault?.type?.ko
            : valueDefault?.type?.en,
        value: valueDefault?.type_id,
        data: {
          id: valueDefault?.type_id,
          name: valueDefault?.type,
        },
      });
    }
  };

  const handleCallBarDetail = async (idItem: string) => {
    try {
      if (!idItem) {
        return;
      }
      setIsLoadingDetail((_prev) => true);
      const resDetail: any = await getBarDetail(idItem);

      if (resDetail?.data) {
        handleSetValueForm(resDetail?.data);
        setIsLoadingDetail((_prev) => false);
      } else {
        setIsLoadingDetail((_prev) => false);
      }
    } catch (error: any) {
      setIsLoadingDetail((_prev) => false);
      return error;
    }
  };

  const handleSetLatLngClick = (lat: any, lng: any) => {
    if (!lat && !lng) return;

    formik.setFieldValue("lat", lat);
    formik.setFieldValue("long", lng);
  };

  const handleVerifyBusinessNumber = async (value: string) => {
    if (!value) return;
    formik.setFieldTouched("business_number", true);
    const validate: any = await postVerifyBusinessNumber({
      b_no: [value],
    });

    if (!validate?.data) {
      formik.setFieldError("business_number", validate?.message);
      return false;
    }

    return true;
  };

  useEffect(() => {
    handleCallBarDetail(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    document.title = `${t("Create Store Information")} - ${t("Bars")} | Baive`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  const getLatLngByAddress = async (address: string) => {
    if (address) {
      const data: any = await userApi.getGEOByAddress({
        query: encodeURIComponent(address),
      });

      let dataAddress = data?.data?.addresses;

      if (dataAddress?.length > 0) {
        const responseAddress = dataAddress?.[0];
        let address = "";

        if (responseAddress.address) {
          address = responseAddress.address;
        } else if (responseAddress.jibunAddress) {
          address = responseAddress.jibunAddress;
        } else if (responseAddress.roadAddress) {
          address = responseAddress.roadAddress;
        } else if (responseAddress.englishAddress) {
          address = responseAddress.englishAddress;
        } else if (responseAddress.jibunAddressEnglish) {
          address = responseAddress.jibunAddressEnglish;
        }

        if (address) {
          formik?.setFieldValue("address", address);
        }

        const sigungu = responseAddress?.addressElements.find((i: any) =>
          i?.types.includes("SIGUGUN")
        );

        const roadname = responseAddress?.addressElements.find((i: any) =>
          i?.types.includes("ROAD_NAME")
        );

        const short_address = [sigungu?.shortName, roadname?.shortName].join(
          " "
        );

        formik?.setFieldValue("short_address", short_address);

        if (responseAddress?.x || responseAddress?.y) {
          formik?.setFieldValue("lat", responseAddress?.y);
          formik?.setFieldValue("long", responseAddress?.x);
        }
      }
    }
  };

  const handleCloseModalAddress = () => {
    setIsOpenSearchAddress(false);
  };

  const handleOpenSearchAddress = () => {
    setIsOpenSearchAddress(true);
  };

  const onSetValueAddress = (data: any) => {
    if (data) {
      getLatLngByAddress(data);
    }
    setIsOpenSearchAddress(false);
  };

  return (
    <React.Fragment>
      <div className="mt-3">
        <>
          <form onSubmit={formik.handleSubmit}>
            <Row>
              <Col lg={12}>
                <div className="mb-3">
                  <Label htmlFor="name-bar" className="form-label">
                    {t("Store Name")} <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="name-bar"
                    name="title"
                    type="text"
                    className="form-control"
                    placeholder={t("Enter your store name")}
                    value={formik?.values?.title}
                    onChange={(event: any) =>
                      formik.setFieldValue("title", event?.target?.value)
                    }
                  />
                  {formik.touched.title && formik.errors.title ? (
                    <div className="text-danger mt-2">
                      {formik.errors.title}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col lg={6}>
                <div className="mb-3">
                  <Label htmlFor="description" className="form-label">
                    {t("Description")}
                  </Label>
                  <Input
                    name="description"
                    type="text"
                    className="form-control"
                    id="description"
                    placeholder={t("Enter your description")}
                    value={formik?.values?.description}
                    onChange={(event: any) =>
                      formik.setFieldValue("description", event?.target?.value)
                    }
                  />
                  {formik.touched.description && formik.errors.description ? (
                    <div className="text-danger mt-2">
                      {formik.errors.description}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col lg={6}>
                <div className="mb-3">
                  <Label htmlFor="phone" className="form-label">
                    {t("Phone Number")}
                  </Label>
                  <Input
                    name="phone"
                    type="text"
                    className="form-control"
                    id="phone"
                    placeholder={t("Enter your phone number")}
                    value={formik?.values?.phone}
                    onChange={(event: any) =>
                      formik.setFieldValue("phone", event?.target?.value)
                    }
                  />
                  {formik.touched.phone && formik.errors.phone ? (
                    <div className="text-danger mt-2">
                      {formik.errors.phone}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col lg={6}>
                <div className="mb-3">
                  <Label htmlFor="business_number" className="form-label">
                    {t("Business Registration Number")}
                  </Label>

                  <Input
                    name="business_number"
                    type="text"
                    className="form-control"
                    id="business_number"
                    placeholder={t("Enter business registration number")}
                    disabled
                    value={formik?.values?.business_number}
                    onChange={(event: any) =>
                      formik.setFieldValue(
                        "business_number",
                        event?.target?.value
                      )
                    }
                    // onBlur={handleVerifyBusinessNumber}
                    onBlur={(e) => {
                      handleVerifyBusinessNumber(e?.target?.value);
                      formik.handleBlur(e);
                    }}
                  />

                  {formik.touched.business_number &&
                    formik.errors.business_number ? (
                    <div className="text-danger mt-2">
                      {formik.errors.business_number}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col lg={6}>
                <div className="mb-3">
                  <Label htmlFor="representative_name" className="form-label">
                    {t("Director Name")}
                  </Label>
                  <Input
                    name="representative_name"
                    type="text"
                    className="form-control"
                    id="representative_name"
                    placeholder={t("Enter director name")}
                    value={formik?.values?.representative_name}
                    onChange={(event: any) =>
                      formik.setFieldValue(
                        "representative_name",
                        event?.target?.value
                      )
                    }
                  />
                  {formik.touched.representative_name &&
                    formik.errors.representative_name ? (
                    <div className="text-danger mt-2">
                      {formik.errors.representative_name}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col lg={12}>
                <div className="mb-3">
                  <Label className="form-label">{t("Hashtag")}</Label>

                  <Keywords
                    name="keywords"
                    placeholder={`${t("Enter your hashtag")}...`}
                    suggestions={[]}
                    initialValue={initialValueHashTag}
                    onChangeTag={(event) => {
                      const keywords = event?.map((item) => item.text);
                      formik.setFieldValue("hashtag", keywords);
                    }}
                  />

                  {formik?.touched?.hashtag && formik?.errors?.hashtag ? (
                    <div className="text-danger mt-2">
                      {formik?.errors?.hashtag}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col lg={3}>
                <div className="mb-3">
                  <Label className="form-label">{t("Store Type")}</Label>

                  <DropdownAsyncBarType
                    name="type"
                    onChangeSelect={(event: any) => {
                      formik.setFieldValue("type", event?.value);
                      setCheckedBarType(event);
                    }}
                    isMulti={false}
                    placeholder={`${t("Select Type")}...`}
                    isShowIconCheck={false}
                    isAllOption={false}
                    listCheckedBarType={checkedBarType}
                    hideSelectedOptions={true}
                    key={"bar-type"}
                  />

                  {formik?.touched?.type && formik?.errors?.type ? (
                    <div className="text-danger mt-2">
                      {/* {formik?.errors?.type} */}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col lg={3}>
                <div className="mb-3">
                  <Label className="form-label">{t("Plan")}</Label>

                  <DropdownStatusNew
                    name="plan"
                    dataList={LIST_OPTION_BAR_LIST_PLAN || []}
                    placeholder={`${t("Select Plan")}...`}
                    className="dropdown-status-rounded"
                    classNamePrefix="name-prefix"
                    initialValue={formik?.values?.plan || null}
                    onChangeSelect={(event: any) =>
                      formik.setFieldValue("plan", event?.value)
                    }
                    isHasOptionAll={false}
                    optionAll={{ label: t("All Filter"), value: "" }}
                    colors={COLOR_BAR_LIST_PLAN}
                  />
                  {formik?.touched?.plan && formik?.errors?.plan ? (
                    <div className="text-danger mt-2">
                      {/* {formik?.errors?.plan} */}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col lg={3}>
                <div className="mb-3">
                  <Label className="form-label">{t("Status")}</Label>

                  <DropdownStatusNew
                    name="status"
                    dataList={LIST_OPTION_BAR_LIST_OPEN_STATUS || []}
                    placeholder={`${t("Select Open Status")}...`}
                    className="dropdown-status-rounded"
                    classNamePrefix="name-prefix"
                    initialValue={formik?.values?.is_active}
                    onChangeSelect={(event: any) =>
                      formik.setFieldValue("is_active", event?.value)
                    }
                    isHasOptionAll={false}
                    colors={COLOR_BAR_LIST_OPEN_STATUS}
                  />

                  {formik?.touched?.plan && formik?.errors?.plan ? (
                    <div className="text-danger mt-2">
                      {/* {formik?.errors?.plan} */}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col lg={3}>
                <div className="mb-3">
                  <Label className="form-label">{t("Partner Status")}</Label>

                  <DropdownStatusNew
                    name="partner_status"
                    dataList={LIST_OPTION_BAR_LIST_PARTNER_STATUS || []}
                    placeholder={`${t("Partner Status")}...`}
                    className="dropdown-status-rounded"
                    classNamePrefix="name-prefix"
                    initialValue={formik?.values?.partner_status}
                    onChangeSelect={(event: any) =>
                      formik.setFieldValue("partner_status", event?.value)
                    }
                    isHasOptionAll={false}
                    optionAll={{ label: t("All Filter"), value: "" }}
                    colors={COLOR_BAR_LIST_PARTNER_STATUS}
                  />

                  {formik?.touched?.plan && formik?.errors?.plan ? (
                    <div className="text-danger mt-2">
                      {/* {formik?.errors?.plan} */}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col lg={12}>
                <div className="mb-3">
                  <Label htmlFor="address" className="form-label">
                    {t("Address")}
                    <span className="text-danger"> *</span>
                  </Label>

                  <div className="d-flex mb-2 gap-2">
                    <Input
                      name="address"
                      type="text"
                      className="form-control"
                      id="address"
                      placeholder={t("Enter your address")}
                      value={formik?.values?.address}
                      onChange={(event: any) =>
                        formik.setFieldValue("address", event?.target?.value)
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-primary whitespace-nowrap"
                      onClick={handleOpenSearchAddress}
                    >
                      {t("Find Address")}
                    </button>
                  </div>

                  {formik.touched.address && formik.errors.address ? (
                    <div className="text-danger mt-2">
                      {formik.errors.address}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col lg={6}>
                <div className="mb-3">
                  <Label htmlFor="address" className="form-label">
                    {t("Short Address")}
                  </Label>
                  <Input
                    name="short_address"
                    type="text"
                    className="form-control"
                    id="short_address"
                    placeholder={t("Enter your short address")}
                    value={formik?.values?.short_address}
                    onChange={(event: any) =>
                      formik.setFieldValue(
                        "short_address",
                        event?.target?.value
                      )
                    }
                  />
                </div>
              </Col>

              <Col lg={3}>
                <div className="mb-3">
                  <Label htmlFor="address" className="form-label">
                    {t("Latitude")}
                  </Label>
                  <Input
                    name="lat"
                    type="text"
                    className="form-control"
                    id="lat"
                    placeholder={t("Enter latitude")}
                    value={formik?.values?.lat}
                    onChange={(event: any) =>
                      formik.setFieldValue("lat", event?.target?.value)
                    }
                  />
                </div>
              </Col>

              <Col lg={3}>
                <div className="mb-3">
                  <Label htmlFor="address" className="form-label">
                    {t("Longitude")}
                  </Label>
                  <Input
                    name="long"
                    type="text"
                    className="form-control"
                    id="long"
                    placeholder={t("Enter your longitude")}
                    value={formik?.values?.long}
                    onChange={(event: any) =>
                      formik.setFieldValue("long", event?.target?.value)
                    }
                  />
                </div>
              </Col>

              {/* <Col lg={4}>
                <div className="mb-3">
                  <Label htmlFor="region1" className="form-label">
                    {t("Province/Metropolitan City")}
                  </Label>
                  <Input
                    name="region1"
                    type="text"
                    className="form-control"
                    id="region1"
                    placeholder={t("Enter Province/Metropolitan City")}
                    value={formik?.values?.region1}
                    onChange={(event: any) =>
                      formik.setFieldValue("region1", event?.target?.value)
                    }
                  />
                </div>
              </Col>

              <Col lg={4}>
                <div className="mb-3">
                  <Label htmlFor="region2" className="form-label">
                    {t("City/District")}
                  </Label>
                  <Input
                    name="region2"
                    type="text"
                    className="form-control"
                    id="region2"
                    placeholder={t("Enter City/District")}
                    value={formik?.values?.region2}
                    onChange={(event: any) =>
                      formik.setFieldValue("region2", event?.target?.value)
                    }
                  />
                </div>
              </Col>

              <Col lg={4}>
                <div className="mb-3">
                  <Label htmlFor="region3" className="form-label">
                    {t("Town/Neighborhood")}
                  </Label>
                  <Input
                    name="region3"
                    type="text"
                    className="form-control"
                    id="region3"
                    placeholder={t("Enter Town/Neighborhood")}
                    value={formik?.values?.region3}
                    onChange={(event: any) =>
                      formik.setFieldValue("region3", event?.target?.value)
                    }
                  />
                </div>
              </Col> */}

              <Col lg={6}>
                <div className="mb-3">
                  <Label htmlFor="long" className="form-label">
                    {t("Location")}
                  </Label>

                  <div>
                    <NaverMap
                      nameAddress={formik?.values?.title}
                      latDefault={formik?.values?.lat}
                      lngDefault={formik?.values?.long}
                      handleSetLatLngClick={handleSetLatLngClick}
                    />
                  </div>
                  {formik.touched.long && formik.errors.long ? (
                    <div className="text-danger mt-2">{formik.errors.long}</div>
                  ) : null}
                </div>
              </Col>

              <Col lg={6} className="d-flex flex-column justify-content-between">
                <Row>
                  <Col md={12} lg={6} xxl={6}>
                    <div className="mb-3">
                      <label className="form-label">
                        {t("Thumbnail")}
                        <span className="text-danger"> *</span>
                      </label>

                      <UploadImageSingle
                        data={formik.getFieldProps("thumbnail_url")?.value}
                        setLinkImage={setLinkImage}
                        formik={formik}
                        isMultiple={false}
                        formikNameImage="thumbnail_url"
                        isPage="bar"
                      />
                    </div>
                  </Col>

                  <Col md={12} lg={6} xxl={6}>
                    <div className="mb-3">
                      <label className="form-label">
                        {t("Background")}
                        <span className="text-danger"> *</span>
                      </label>

                      <UploadImage
                        data={formik.getFieldProps("background_url")?.value}
                        setLinkImage={setLinkImage}
                        formik={formik}
                        formikNameImage="background_url"
                      />

                      {formik.touched.background_url &&
                        formik.errors.background_url ? (
                        <div className="text-danger mt-2">
                          {formik.errors.background_url}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col md={12} lg={12} xxl={12}>
                    <div className="mb-3">
                      <label className="form-label">{t("Photos")}</label>
                      <UploadImage
                        data={formik.getFieldProps("files")?.value}
                        setLinkImage={setLinkImage}
                        formik={formik}
                      // isUploadMore={true}
                      />
                    </div>
                  </Col>
                </Row>

                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end mb-3">
                    {
                      <button
                        type="submit"
                        className="btn btn-primary fs-14"
                      // disabled={Object.keys(formik.errors).length !== 0}
                      >
                        {isLoading ? (
                          <Spinner size="sm" className="me-2"></Spinner>
                        ) : !!id ? (
                          <i className="ri-login-circle-line align-bottom me-2 "></i>
                        ) : (
                          <i className="ri-add-fill align-bottom me-2"></i>
                        )}
                        {id ? t("Button Update") : t("Create")}
                      </button>
                    }

                    {/* <button
                    className="btn btn-soft-secondary fs-14"
                    type="button"
                    color="light"
                    onClick={() => {}}
                    disabled={isLoading}
                  >
                    <i className="ri-indeterminate-circle-line align-bottom me-1"></i>
                    {t("Button Close")}
                  </button> */}
                  </div>
                </Col>
              </Col>
            </Row>
          </form>
          {isOpenSearchAddress && (
            <Modal
              isOpen={!!isOpenSearchAddress}
              centered={true}
              size="lg"
              scrollable={false}
            >
              <ModalHeader toggle={handleCloseModalAddress}>
                {"Search Address"}
              </ModalHeader>
              <ModalBody>
                <SearchAddress onSetValueAddress={onSetValueAddress} />
              </ModalBody>
            </Modal>
          )}
        </>
      </div>
    </React.Fragment>
  );
};

export default BarDetailForm;
